import type { WorldConfig } from '../../config/world';
import { computeSky } from '../astro/sky';
import { dayOfYear, instantOfWallTime, parseWallTime, wallTimeOf } from '../time/zoned';
import { weatherAt } from '../weather/interpolate';
import { HOUR_MS, type WeatherHistory } from '../weather/types';
import { WORLD_STATE_VERSION, type WorldMemory, type WorldOverrides, type WorldState } from './types';

const YEAR_MS = 365.2425 * 86_400_000;
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

function sumOver(history: WeatherHistory, instant: number, hours: number, key: 'snowfallCm' | 'rainMm'): number {
  const from = instant - hours * HOUR_MS;
  let sum = 0;
  for (const h of history.hours) if (h.time > from && h.time <= instant) sum += h[key];
  return sum;
}

/** Phase-1 placeholder memory model; phase 5 replaces it with proper accumulation/drying/melting. */
function deriveMemory(history: WeatherHistory, instant: number, temperatureC: number): WorldMemory {
  return {
    wetness: clamp01(sumOver(history, instant, 6, 'rainMm') / 2),
    puddles: 0,
    snowCover: temperatureC <= 2 ? clamp01(sumOver(history, instant, 48, 'snowfallCm') / 5) : 0,
    frost: 0,
    groundLitter: { petals: 0, leaves: 0 },
  };
}

export function deriveWorldState(
  instant: number,
  history: WeatherHistory,
  config: WorldConfig,
  overrides?: WorldOverrides,
): WorldState {
  const local = wallTimeOf(instant, config.timeZone);
  const epochWall = parseWallTime(config.worldEpoch);
  if (!epochWall) throw new Error(`Invalid worldEpoch: ${config.worldEpoch}`);
  const epoch = instantOfWallTime(epochWall, config.timeZone);
  const weather = weatherAt(history, instant);
  const memory = deriveMemory(history, instant, weather.temperatureC);

  return {
    version: WORLD_STATE_VERSION,
    time: { instant, local, dayOfYear: dayOfYear(local), yearsSinceEpoch: (instant - epoch) / YEAR_MS },
    sky: computeSky(instant, config.location),
    weather,
    memory: { ...memory, ...overrides?.memory },
    season: { ...overrides?.season },
    events: [],
    meteors: [],
  };
}
